import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getData, getDataById, updateData, getcountries, getpaymentType } from '../../../services/management/paymentReceipt/paymentReceiptReceivedServices';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

// ── Identify the current management user ─────────────────────────────────────
const currentUserName = `management`;

// ── Parse remarks stored as JSON string or array ──────────────────────────────
const parseRemarksHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return [];
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ─────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const PaymentReceiptReceived = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({
    id: '',
    accommodation: '',
    accommodation_status: '',
    billing_address: '',
    billing_countries: '',
    billing_email: '',
    billing_full_name: '',
    billing_phone_no: '',
    createdAt: '',
    currency_type: '',
    date_of_payment: '',
    event_date: '',
    event_title: '',
    event_type: '',
    no_of_ticket: '',
    occupancy_type: '',
    payment_id: '',
    payment_type: '',
    participant_type: '',
    pdf_status: '',
    receipt_date: '',
    refund_status: '',
    reg_amount: '',
    reg_amount_usd: '',
    reg_category: '',
    reg_type: '',
    series: '',
    shipping: '',
    shipping_address: '',
    shipping_countries: '',
    shipping_email: '',
    shipping_full_name: '',
    shipping_phone_no: '',
    sub_domain: '',
    subject: '',
    total_no_of_nights: '',
    transaction_id: '',
    updatedAt: '',
  });
  const [modalType, setModalType]         = useState('');
  const [showModal, setShowModal]         = useState(false);
  const [searchText, setSearchText]       = useState('');
  const [currentPage, setCurrentPage]     = useState(1);
  const [loading, setLoading]             = useState(true);
  const [dropdownData, setDropdownData]   = useState({ countries: [], paymentType: [] });

  // ── Remarks state ─────────────────────────────────────────────────────────
  const [remarks,        setRemarks]        = useState('');
  const [remarksHistory, setRemarksHistory] = useState([]);
  const [remarksError,   setRemarksError]   = useState('');

  // ── Fetch dropdown data ───────────────────────────────────────────────────
  const fetchDropdownData = useCallback(async () => {
    try {
      const [country, payment] = await Promise.all([getcountries(), getpaymentType()]);
      setDropdownData({ countries: country, paymentType: payment });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  // ── Fetch list ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ pdf_status: 0 });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    init();
  }, [fetchData, fetchDropdownData]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.transaction_id.trim()) {
      alert('Transaction ID is required');
      return;
    }

    // Remarks is mandatory ONLY when history already exists
    if (remarksHistory.length > 0 && !remarks.trim()) {
      setRemarksError('Remarks is required');
      return;
    }

    try {
      let updatedRemarks = [...remarksHistory];

      // Append new remark if the user entered one
      if (remarks.trim()) {
        updatedRemarks = [
          ...remarksHistory,
          {
            remark:    remarks.trim(),
            timestamp: new Date().toISOString(),
            user:      currentUserName,
          },
        ];
      }

      if (modalType === 'edit') {
        await updateData({
          id:             form.id,
          transaction_id: form.transaction_id,
          remarks:        JSON.stringify(updatedRemarks),
        });
      }

      await fetchData();
      setShowModal(false);
      pdfModal('edit', form);
    } catch (error) {
      console.error('Error updating transaction ID:', error);
    }
  };

  const navigate = useNavigate();

  const pdfModal = async (type, row) => {
    try {
      const data = await getDataById({ id: row.id });
      if (data) {
        navigate('/mgmtPaymentReceiptPdf', { state: { data } });
      }
    } catch (error) {
      console.error('Error fetching data by ID:', error);
    }
  };

  const openModal = (type, data = {
    id: '',
    accommodation : '',
    accommodation_status : '',
    billing_address : '',
    billing_countries : '',
    billing_email : '',
    billing_full_name : '',
    billing_phone_no : '',
    createdAt : '',
    currency_type : '',
    date_of_payment : '',
    event_date : '',
    event_title : '',
    event_type : '',
    no_of_ticket : '',
    occupancy_type : '',
    payment_id : '',
    payment_type : '',
    participant_type: '',
    pdf_status : '',
    receipt_date : '',
    refund_status : '',
    reg_amount : '',
    reg_amount_usd : '',
    reg_category : '',
    reg_type : '',
    series : '',
    shipping : '',
    shipping_address : '',
    shipping_countries : '',
    shipping_email : '',
    shipping_full_name : '',
    shipping_phone_no : '',
    sub_domain : '',
    subject : '',
    total_no_of_nights : '',
    transaction_id : '',
    updatedAt : ''
    }) => {
    setModalType(type);
    setForm(data);

    // ── Parse and reset remarks ───────────────────────────────────────────
    setRemarksHistory(parseRemarksHistory(data.remarks));
    setRemarks('');
    setRemarksError('');

    setShowModal(true);
  };

  const resetModal = () => {
    setShowModal(false);
    setRemarks('');
    setRemarksHistory([]);
    setRemarksError('');
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'INR': return '₹';
      case 'GBP': return '£';
      case 'RUB': return '₽';
      case 'THB': return '฿';
      default:    return currencyCode || '';
    }
  };

  const getCountryName = (code) => {
    const country = dropdownData.countries.find((c) => c.code === code);
    return country ? country.name : code;
  };

  const getPaymentTypeName = (code) => {
    const payment = dropdownData.paymentType.find((c) => c.code === code);
    return payment ? payment.type : code;
  };

  // ── Filtered & sorted list ────────────────────────────────────────────────
  const filteredData = useMemo(() =>
    dataList
      .filter(
        (data) =>
          data.billing_full_name.toLowerCase().includes(searchText.toLowerCase()) ||
          data.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
          data.reg_amount.toString().includes(searchText.toLowerCase()) ||
          data.currency_type.toLowerCase().includes(searchText.toLowerCase()) ||
          data.event_type.toLowerCase().includes(searchText.toLowerCase()) ||
          data.date_of_payment.toString().includes(searchText.toLowerCase()),
      )
      .sort((a, b) => new Date(b.date_of_payment) - new Date(a.date_of_payment)),
  [dataList, searchText]);

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      name: '#',
      selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
      sortable: false,
      width: '60px',
    },
    { name: 'Date of Payment', selector: (row) => row.date_of_payment, sortable: true },
    {
      name: 'Series',
      selector: (row) => {
        const date   = new Date(row.series);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[date.getMonth()]} ${date.getFullYear()}`;
      },
      sortable: true,
    },
    { name: 'Sub domain',  selector: (row) => row.sub_domain,        sortable: true },
    { name: 'Name',        selector: (row) => row.billing_full_name,  sortable: true },
    { name: 'Country',     selector: (row) => getCountryName(row.billing_countries), sortable: true },
    {
      name: 'Reg Amount',
      selector: (row) => (
        <b className="fs-4">{getCurrencySymbol(row.currency_type)} {row.reg_amount}</b>
      ),
      sortable: true,
    },
    { name: 'Event Type', selector: (row) => row.event_type, sortable: true },
    {
      name: 'Actions',
      cell: (row) => (
        <Button variant="danger" size="sm" onClick={() => openModal('edit', row)}>
          <i className="align-middle far fa-fw fa-file-pdf"></i>PDF
        </Button>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />

        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">PDF Generation / Payment Receipt / Received Receipt</p>
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationComponentOptions={{ noRowsPerPage: true }}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Search"
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                }
              />

              {/* ── Modal ── */}
              <Modal className="modal-lg" backdrop="static" show={showModal} onHide={resetModal}>
                <Modal.Header closeButton>
                  <Modal.Title>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  {/* Details table */}
                  <Table striped bordered hover responsive className="table-sm">
                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Payment Receipt Details</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Event Type</td>       <td>{form.event_type}</td></tr>
                      <tr><td>Receipt Date</td>      <td>{form.receipt_date}</td></tr>
                      <tr><td>Payment ID</td>        <td>{form.payment_id}</td></tr>
                      <tr><td>Participant Type</td>  <td>{form.participant_type}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Billing Address</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Full Name</td> <td>{form.billing_full_name}</td></tr>
                      <tr><td>Email</td>     <td>{form.billing_email}</td></tr>
                      <tr><td>Phone No</td>  <td>{form.billing_phone_no}</td></tr>
                      <tr><td>Address</td>   <td>{form.billing_address}</td></tr>
                      <tr><td>Country</td>   <td>{getCountryName(form.billing_countries)}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Shipping Address</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Full Name</td> <td>{form.shipping_full_name}</td></tr>
                      <tr><td>Email</td>     <td>{form.shipping_email}</td></tr>
                      <tr><td>Phone No</td>  <td>{form.shipping_phone_no}</td></tr>
                      <tr><td>Address</td>   <td>{form.shipping_address}</td></tr>
                      <tr><td>Country</td>   <td>{getCountryName(form.shipping_countries)}</td></tr>
                    </tbody>

                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Payment Description</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Date of Payment</td>       <td>{form.date_of_payment}</td></tr>
                      <tr><td>Mode of Payment</td>       <td>{getPaymentTypeName(form.payment_type)}</td></tr>
                      <tr><td>Currency</td>              <td>{form.currency_type}</td></tr>
                      <tr><td>Registration Amount</td>   <td>{form.reg_amount}</td></tr>
                      <tr><td>Registration Type</td>     <td>{form.reg_type}</td></tr>
                      <tr><td>Registration Category</td> <td>{form.reg_category}</td></tr>
                      <tr><td>No. of Ticket</td>         <td>{form.no_of_ticket}</td></tr>
                      {form.occupancy_type && (
                        <tr><td>Occupancy</td>     <td>{form.occupancy_type}</td></tr>
                      )}
                      {form.total_no_of_nights && (
                        <tr><td>No. of Nights</td> <td>{form.total_no_of_nights}</td></tr>
                      )}
                    </tbody>
                  </Table>
                  {/* Transaction ID */}
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Transaction Id <small className="text-danger">*</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="transaction_id"
                        value={form.transaction_id}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Form>

                  {/* ── Remarks section — only shown when history exists ── */}
                  {remarksHistory.length > 0 && (
                    <>
                      {/* History */}
                      <div className="col-md-12 mb-3">
                        <Form.Label className="fs-5 text-dark fw-semibold">
                          Remarks History
                          <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                            {remarksHistory.length}
                          </span>
                        </Form.Label>
                        <div
                          className="border rounded"
                          style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                        >
                          {[...remarksHistory].reverse().map((entry, idx) => (
                            <div
                              key={idx}
                              className="px-3 py-2 border-bottom"
                              style={{ borderLeft: '3px solid #0d6efd' }}
                            >
                              <div className="d-flex justify-content-between align-items-baseline">
                                <small className="text-muted d-block mb-1">
                                  🕒 {formatTimestamp(entry.timestamp)}
                                </small>
                                {entry.user && (
                                  <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem' }}>
                                    {entry.user}
                                  </span>
                                )}
                              </div>
                              <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>
                                {entry.remark}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Mandatory remarks input */}
                      <Form.Group className="col-md-12 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Remarks <span className="text-danger fs-4"> * </span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={remarks}
                          placeholder="Enter reason for this action…"
                          onChange={(e) => {
                            setRemarks(e.target.value);
                            setRemarksError('');
                          }}
                          isInvalid={!!remarksError}
                        />
                        <Form.Control.Feedback type="invalid">
                          {remarksError}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </>
                  )}
                </Modal.Body>

                <Modal.Footer>
                  <Button variant="secondary" onClick={resetModal}>Cancel</Button>
                  <Button variant="primary" onClick={handleSubmit}>Generate PDF</Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReceiptReceived;
